import React, { ReactElement } from 'react'
import MobileHeaderClose from 'images/barry/mobile-header-close.svg'
import ControlDLogo from 'images/barry/controld-logo-small.svg'
import SplashScreen from './ChatWindow/Pages/SplashScreen'
import SubmitTicketView from './ChatWindow/Pages/SubmitTicketView'
import TicketSent from './ChatWindow/Pages/TicketSent'
import { useAppSelector } from 'store/hooks'
import { BarryPage } from 'store/barry/barry'
import { Flex, IconButton } from 'theme-ui'
import BarryChatScreen from './ChatWindow/Pages/BarryChatScreen'
import DescribeYourIssue from './ChatWindow/Pages/DescribeYourIssue'
import useBreakpointIndex from 'ui/Theme/useBreakpointIndex'
import { Svg } from 'ui'
import useGetColorMode from 'utils/useGetColorMode'

export default function BarryWindowContainer({
  handleBarryIconToggle,
}: {
  handleBarryIconToggle: () => void
}): ReactElement {
  const page = useAppSelector(x => x.barry.barryPage)
  const isMobile = useBreakpointIndex() === 0
  const { isLightMode } = useGetColorMode()
  const isChatPage = page === BarryPage.CHAT_PAGE

  return (
    <Flex
      id="barry-window"
      data-testid="barry-window"
      sx={{
        zIndex: 'zIndex625',
        margin: 0,
        fontFamily: 'body',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        overflow: 'clip',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'blueYonder30',
        boxShadow: '0px 5px 45px -5px rgba(0, 0, 0, 45%)',
        borderRadius: [0, '1.6rem'],
        transitionProperty: 'opacity',
        transitionDuration: '125ms',
        position: 'fixed',
        height: ['100%', 'calc(100% - 96px)'],
        maxHeight: ['none', '80rem'],
        width: ['100%', '40.8rem', '40.8rem'],
        bottom: [0, `8.4rem`],
        right: [0, '2.4rem'],
        backgroundColor: 'blue',
        justifyContent: 'center',
        '@media screen and (max-height: 450px)': {
          overflow: !isChatPage ? 'scroll' : ' none',
        },
      }}
    >
      <Flex
        sx={{
          height: '7.2rem',
          width: '100%',
          alignItems: 'center',
          zIndex: 'zIndex630',
          justifyContent: ['space-between', 'center'],
          borderBottom: '1px solid',
          borderColor: 'borderFaint',
          flexShrink: 0,
          p: '1.6rem',
          backgroundColor: isLightMode ? 'commonWhite' : 'white6',
          '@media screen and (max-height: 450px)': {
            backgroundColor: 'smokyBlack',
            position: !isChatPage ? 'fixed' : 'static',
            top: 0,
          },
        }}
      >
        <Svg svg={ControlDLogo} fill="aliceBlue" descriptiveText="Control D Logo" />
        {isMobile && (
          <IconButton
            aria-label="barry mobile close button"
            data-testid="barry-mobile-close-button"
            sx={{
              height: '4.8rem',
              width: '4.8rem',
              mr: '-1.5rem',
            }}
            onClick={handleBarryIconToggle}
          >
            <Svg svg={MobileHeaderClose} fill="aliceBlue" descriptiveText="Close Barry window" />
          </IconButton>
        )}
      </Flex>
      <Flex
        data-testid="barry-window-container"
        sx={{
          flex: 1,
          alignItems: 'center',
          backgroundColor: isLightMode && 'ghostWhite',
        }}
      >
        {page === BarryPage.SPLASH_PAGE && <SplashScreen />}
        {page === BarryPage.DESCRIBE_ISSUE_PAGE && <DescribeYourIssue />}
        {page === BarryPage.CHAT_PAGE && (
          <BarryChatScreen handleBarryIconToggle={handleBarryIconToggle} />
        )}
        {page === BarryPage.SUBMIT_TICKET && <SubmitTicketView />}
        {page === BarryPage.TICKET_SENT && (
          <TicketSent handleBarryIconToggle={handleBarryIconToggle} />
        )}
      </Flex>
    </Flex>
  )
}
